/* eslint-disable react/require-default-props, react/prop-types, import/no-named-as-default */
import React, { useEffect, useRef, useState } from 'react';
import { SimpleForm, Toolbar, useTranslate } from 'react-admin';
import { useForm, useFormState, useField } from 'react-final-form';
import { Box, Step, StepLabel, Stepper, makeStyles, useTheme, Fade } from '@material-ui/core';
import PropTypes from 'prop-types';
import { WealthToolbar } from '../guesser/wealth-toolbar';
import Button from '../ra/buttons/Button';

const useStyles = makeStyles({
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    width: '100%',
  },
  previousButton: {
    background: 'rgba(0, 0, 0, 0.38)',
  },
});

const validateFormValueByStep = (validationFields, form) => {
  const invalidFieldName = validationFields?.length
    ? validationFields.find((fieldName) => {
        const fieldState = form?.getFieldState(fieldName);
        return !fieldState?.valid;
      })
    : undefined;

  return invalidFieldName ? form?.getFieldState(invalidFieldName) : undefined;
};

const LastStepToolbar = ({ title, content, onStepChange, currentStep, onCustomSave }) => {
  const translate = useTranslate();
  const classes = useStyles();
  const { values } = useFormState();

  return (
    <WealthToolbar
      onCustomSave={() => onCustomSave(values)}
      alwaysEnableSaveButton
      customChildren={
        <Button
          className={classes.previousButton}
          onClick={() => onStepChange(currentStep - 1)}
          label={translate('ra.action.previous')}
        />
      }
      confirmDialogInfo={{
        title,
        content,
      }}
      isAcl
    />
  );
};

const CommonStepToolbar = ({ fieldsInStep, currentStep, onStepChange }) => {
  const theme = useTheme();
  const form = useForm();
  const translate = useTranslate();
  const classes = useStyles();
  const [invalidField, setInvalidField] = useState();

  return (
    <Box display="flex" alignItems="flex-end" flexDirection="column">
      {!!invalidField && (
        <Fade in={!!invalidField}>
          <Box color={theme.palette.error.main} marginRight="16px">
            {translate(invalidField?.error || 'ra.page.error')}
          </Box>
        </Fade>
      )}
      {invalidField?.name && (
        <OnFieldChange
          watchField={invalidField.name}
          onChange={() => {
            const newInvalidField = validateFormValueByStep(fieldsInStep[currentStep], form);

            setInvalidField(newInvalidField || undefined);
          }}
        />
      )}
      <Toolbar className={classes.toolbar}>
        {currentStep !== 0 && (
          <Button
            className={classes.previousButton}
            onClick={() => onStepChange(currentStep - 1)}
            label={translate('ra.action.previous')}
          />
        )}
        <Button
          onClick={() => {
            const invalidField = validateFormValueByStep(fieldsInStep[currentStep], form);
            if (invalidField) {
              setInvalidField(invalidField);
            } else {
              onStepChange(currentStep + 1);
            }
          }}
          label={translate('ra.action.next')}
        />
      </Toolbar>
    </Box>
  );
};

const MultiStepForm = ({
  children,
  currentStep,
  onCustomSave,
  onStepChange,
  fieldsInStep,
  confirmationMessage,
  steps,
  ...otherProps
}) => {
  const totalStep = steps.length;
  return (
    <SimpleForm
      redirect="show"
      {...otherProps}
      toolbar={
        currentStep === totalStep - 1 ? (
          <LastStepToolbar
            {...confirmationMessage}
            onCustomSave={onCustomSave}
            onStepChange={onStepChange}
            currentStep={currentStep}
          />
        ) : (
          <CommonStepToolbar fieldsInStep={fieldsInStep} currentStep={currentStep} onStepChange={onStepChange} />
        )
      }
    >
      <Box
        sx={{
          minWidth: 500,
          margin: '0 auto 24px',
        }}
      >
        <Stepper activeStep={currentStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      {children}
    </SimpleForm>
  );
};

MultiStepForm.propTypes = {
  currentStep: PropTypes.number.isRequired,
  steps: PropTypes.arrayOf(PropTypes.string).isRequired,
  onCustomSave: PropTypes.func,
  onStepChange: PropTypes.func.isRequired,
  fieldsInStep: PropTypes.shape({
    [PropTypes.number]: PropTypes.arrayOf(PropTypes.string),
  }),
  confirmationMessage: PropTypes.shape({
    title: PropTypes.string,
    content: PropTypes.string,
  }),
};

export default MultiStepForm;

const OnFieldChange = ({ watchField, onChange }) => {
  const {
    input: { value: watchedValue },
  } = useField(watchField);
  const isFirstRun = useRef(true);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    onChangeRef.current?.(watchedValue);
  }, [watchedValue]);

  return null;
};
